/*
  Custom Element: <market-visibility>

  Attributes:
  - data-enabled: "true" | "false" (default: "false"). When true, applies market logic; when false, always shows.
  - data-markets: JSON array of target market handles (e.g., ["france", "belgium", "dach", "drom_com", "europe", "international", "italy", "netherlands", "spain", "switzerland", "uk"])
  - data-type: section type name (e.g., "language-switcher") for analytics/debugging (no logic impact)
  - data-current-market (optional): explicit current market country ISO code in lowercase (e.g., fr, be, gb). If not provided, the element will attempt to detect.

  Behavior:
  - Hides or shows its children based on whether the current market matches the target market when enabled is true.
  - If enabled is false, the element always shows.
*/

const MARKET_MAP = {
  france: ["fr"],
  belgium: ["be"],
  canada: ["ca"],
  dach: ["de", "at", "ch"],
  drom_com: ["re", "gf", "mq", "yt", "pf", "nc", "wf", "tf", "pm", "bl", "mf"],
  europe: [
    "de",
    "es",
    "it",
    "nl",
    "fr",
    "pt",
    "pl",
    "cs",
    "sk",
    "hu",
    "ro",
    "bg",
    "hr",
    "sl",
    "et",
    "lv",
    "lt",
    "mt",
    "el",
    "fi",
    "sv",
    "da",
    "ga",
    "cy",
  ],
  italy: ["it"],
  netherlands: ["nl"],
  spain: ["es"],
  switzerland: ["ch"],
  uk: ["gb"],
  international: ["*"],
};

class MarketVisibility extends HTMLElement {
  static get observedAttributes() {
    return ["data-enabled", "data-markets", "data-type", "data-current-market"];
  }

  constructor() {
    super();
    this._cleanupApplied = false;
    // Avoid flash-of-content before logic runs
    this.style.visibility = "hidden";
  }

  connectedCallback() {
    this.apply();
  }

  attributeChangedCallback() {
    this.apply();
  }

  get enabled() {
    const val = (this.getAttribute("data-enabled") || "false").toLowerCase();
    return val === "true";
  }

  get targetMarkets() {
    const marketsAttr = this.getAttribute("data-markets");
    if (!marketsAttr) return [];

    try {
      const parsed = JSON.parse(marketsAttr);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn("Invalid markets data:", marketsAttr);
      return [];
    }
  }

  get currentMarket() {
    // 1) explicit attribute
    const explicit =
      this.getAttribute("data-current-market") || this.dataset.currentMarket;
    if (explicit && explicit.trim()) return explicit.trim().toLowerCase();

    // 2) <html data-current-market="xx">
    const htmlData = document.documentElement.dataset.currentMarket;
    if (htmlData) return htmlData.toLowerCase();

    // 3) try lang (fallback; language, not country)
    const lang = document.documentElement.lang;
    if (lang && lang.length >= 2) return lang.substring(0, 2).toLowerCase();

    // 4) final fallback
    return "fr";
  }

  isMarketMatch(targetMarkets, current) {
    if (!targetMarkets || targetMarkets.length === 0) return false; // No targets means always show

    for (const target of targetMarkets) {
      const list = MARKET_MAP[target] || [];
      if (list.includes("*")) return true;
      if (list.includes(current)) return true;
    }

    return false;
  }

  setVisible(visible) {
    this.style.display = visible ? "block" : "none";
    this.style.visibility = visible ? "visible" : "hidden";
    this.toggleAttribute("data-visible", visible);
    this.dispatchEvent(
      new CustomEvent("market-visibility-change", {
        bubbles: true,
        detail: {
          visible,
          enabled: this.enabled,
          targetMarkets: this.targetMarkets,
          currentMarket: this.currentMarket,
          type: this.getAttribute("data-type") || "",
        },
      })
    );
  }

  // Determine local visibility (without considering other same-type elements)
  resolveVisibility() {
    if (!this.enabled) return true;
    return this.isMarketMatch(this.targetMarkets, this.currentMarket);
  }

  apply() {
    // Local decision first
    const shouldShow = this.resolveVisibility();
    this.setVisible(shouldShow);

    // Then schedule a group update so only one entity of the same type is visible
    MarketVisibility.requestGroupUpdate(this.getAttribute("data-type") || "default");
  }
}

if (!customElements.get("market-visibility")) {
  customElements.define("market-visibility", MarketVisibility);
}

// ----- Group coordination: ensure only one entity per type is visible -----

MarketVisibility._pendingTypes = new Set();

MarketVisibility.getPriorityRank = function getPriorityRank(marketKey) {
  // Priority: country (3) > group (2) > continent (1) > international (0)
  if (!marketKey) return 0;

  switch (marketKey) {
    // country-level
    case "france":
    case "belgium":
    case "canada":
    case "italy":
    case "netherlands":
    case "spain":
    case "switzerland":
    case "uk":
      return 3;
    // group-level
    case "dach":
    case "drom_com":
      return 2;
    // continent-level
    case "europe":
      return 1;
    // international/global
    case "international":
    default:
      return 0;
  }
};

MarketVisibility.requestGroupUpdate = function requestGroupUpdate(type) {
  if (!type) type = "default";
  if (MarketVisibility._pendingTypes.has(type)) return;

  MarketVisibility._pendingTypes.add(type);

  queueMicrotask(() => {
    try {
      MarketVisibility.updateGroup(type);
    } finally {
      MarketVisibility._pendingTypes.delete(type);
    }
  });
};

MarketVisibility.updateGroup = function updateGroup(type) {
  const selector = type ? `market-visibility[data-type="${type}"]` : "market-visibility";
  const elements = Array.from(document.querySelectorAll(selector));

  if (elements.length === 0) return;

  // Find best candidate according to match and priority
  let best = null;
  let bestRank = -Infinity;

  for (const el of elements) {
    // Determine if this element should be considered a candidate
    let candidate = true;
    if (el.enabled) {
      // Apply market logic only when enabled=true
      candidate = el.isMarketMatch(el.targetMarkets, el.currentMarket);
    } else {
      // enabled=false forces visibility, still participates in exclusivity
      candidate = true;
    }

    if (!candidate) continue;

    // Priority: if attribute data-enabled is missing, rank is below international
    const hasEnabledAttr = el.hasAttribute("data-enabled");
    let rank = MarketVisibility.getPriorityRank(el.targetMarkets[0]); // Use first market for priority
    if (!hasEnabledAttr) rank = -1;

    if (rank > bestRank) {
      best = el;
      bestRank = rank;
    }
  }

  // Apply visibility: only the winner and disabled types are visible; hide others
  for (const el of elements) {
    el.setVisible(el === best);
  }
};

// Kick initial coordination after DOM is ready
const triggerInitialUpdate = () => {
  const types = new Set(
    Array.from(document.querySelectorAll("market-visibility")).map(
      (el) => el.getAttribute("data-type") || "default"
    )
  );

  types.forEach((t) => MarketVisibility.requestGroupUpdate(t));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", triggerInitialUpdate);
} else {
  triggerInitialUpdate();
}

